import { Response, NextFunction } from 'express';
import { AuthRequest } from './auth.middleware';
import { prisma } from '../shared/prisma';
import { AppError } from '../shared/errors/AppError';

// Static permission map for named preset roles
const PERMISSION_MAP: Record<string, string[]> = {
  super_admin: ['*'],
  admin: ['*'],
  team_owner: [
    'view_activity', 'view_chat_users', 'chat_send_message', 'chat_receive_message', 'chat_edit_message', 'chat_react_message', 'chat_delete_message', 'chat_pin_message', 'chat_forward_message', 'chat_reply_message', 'chat_file_sharing', 'chat_send_attention', 'chat_text_editing', 'chat_use_emoji',
    'create_teams', 'edit_teams', 'delete_teams', 'manage_team_members', 'team_send_message', 'team_receive_message', 'team_edit_message', 'team_react_message', 'team_delete_message', 'team_pin_message', 'team_forward_message', 'team_reply_message', 'team_file_sharing', 'team_text_editing', 'team_use_emoji',
    'view_users_list'
  ],
  member: [
    'view_activity', 'view_chat_users', 'chat_send_message', 'chat_receive_message', 'chat_edit_message', 'chat_react_message', 'chat_reply_message', 'chat_file_sharing', 'chat_send_attention', 'chat_text_editing', 'chat_use_emoji',
    'team_send_message', 'team_receive_message', 'team_edit_message', 'team_react_message', 'team_reply_message', 'team_file_sharing', 'team_text_editing', 'team_use_emoji',
    'view_users_list'
  ],
  guest: [
    'view_activity', 'chat_receive_message', 'chat_react_message', 'team_receive_message', 'team_react_message'
  ],
  department_restricted: [
    'view_activity', 'view_chat_users', 'chat_send_message', 'chat_receive_message', 'chat_react_message', 'chat_reply_message', 'team_send_message', 'team_receive_message', 'team_react_message', 'team_reply_message'
  ]
};

export function requirePermission(...permissions: string[]) {
  return async (req: AuthRequest, _res: Response, next: NextFunction) => {
    try {
      if (!req.user) throw new AppError(401, 'Unauthorized');

      const userRoles = await prisma.userRole.findMany({
        where: { userId: req.user.sub },
        include: { role: true },
      });

      const userPermissions = new Set<string>();
      let hasCustomOverride = false;
      const customPerms = new Set<string>();
      
      for (const ur of userRoles) {
        if (ur.role.name.startsWith('custom_')) {
          const dbPerms = ur.role.permissions;
          if (Array.isArray(dbPerms)) {
            hasCustomOverride = true;
            (dbPerms as string[]).forEach((p) => customPerms.add(p));
          }
        }
      }

      if (hasCustomOverride) {
        customPerms.forEach(p => userPermissions.add(p));
      } else {
        for (const ur of userRoles) {
          if (!ur.role.name.startsWith('custom_')) {
            const staticPerms = PERMISSION_MAP[ur.role.name] ?? [];
            staticPerms.forEach((p) => userPermissions.add(p));
          }
        }
      }

      if (userPermissions.has('*')) return next();

      const hasAll = permissions.every((p) => userPermissions.has(p));
      if (!hasAll) throw new AppError(403, 'Insufficient permissions');

      next();
    } catch (err) {
      next(err);
    }
  };
}

export function requireRole(...roles: string[]) {
  return async (req: AuthRequest, _res: Response, next: NextFunction) => {
    try {
      if (!req.user) throw new AppError(401, 'Unauthorized');

      const userRoles = await prisma.userRole.findMany({
        where: { userId: req.user.sub },
        include: { role: true },
      });

      const roleNames = userRoles.map((ur) => ur.role.name);
      const hasRole = roles.some((r) => roleNames.includes(r));
      if (!hasRole) throw new AppError(403, 'Role not permitted');

      next();
    } catch (err) {
      next(err);
    }
  };
}


